import React, { useEffect, useId, useRef, useState, forwardRef } from 'react';

import { format, isValid, parse } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { ja } from 'react-day-picker/locale';

interface DatePickerProps
  extends Omit<React.HTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  onChange?: (event: { target: { value?: Date } }) => void;
  value?: string;
  name?: string;
  onDateChange?: (date: string) => void;
  minDate?: Date; // Add minDate prop to prevent selecting dates before this date
}

const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  ({ onChange, value, onDateChange, minDate, ...props }, ref) => {
    console.log('DatePicker props:', { onChange, value, ...props });

    const dialogRef = useRef<any>(null);
    const dialogId = useId();
    const headerId = useId();

    // Hold the month in state to control the calendar when the input changes
    const [month, setMonth] = useState(new Date());

    // Parse the string value to Date
    const parsedValue = value
      ? parse(value, 'yyyy-MM-dd', new Date())
      : undefined;
    const isValidValue = parsedValue && isValid(parsedValue);

    // Hold the selected date in state
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(
      isValidValue ? parsedValue : undefined
    );

    // Hold the input value in state (format for display)
    const [inputValue, setInputValue] = useState(
      isValidValue ? format(parsedValue!, 'yyyy/MM/dd') : ''
    );

    // Hold the dialog visibility in state
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // Effect to sync internal state with external value changes
    useEffect(() => {
      console.log('DatePicker useEffect - value changed to:', value);
      const newParsedValue = value
        ? parse(value, 'yyyy-MM-dd', new Date())
        : undefined;
      const newIsValidValue = newParsedValue && isValid(newParsedValue);

      setSelectedDate(newIsValidValue ? newParsedValue : undefined);
      setInputValue(
        newIsValidValue ? format(newParsedValue!, 'yyyy/MM/dd') : ''
      );
    }, [value]);

    // Function to toggle the dialog visibility
    const toggleDialog = () => setIsDialogOpen(!isDialogOpen);

    // Hook to handle the body scroll behavior and focus trapping
    useEffect(() => {
      if (!dialogRef.current) return;
      if (isDialogOpen) {
        dialogRef.current.showModal();
      } else {
        dialogRef.current.close();
      }
    }, [isDialogOpen]);

    const handleDayPickerSelect = (date: Date | undefined) => {
      if (!date) {
        setInputValue('');
        setSelectedDate(undefined);
        // Trigger onChange with empty value
        const event = { target: { value: undefined } };
        console.log('Triggering onChange with empty value');
        console.log('Event object:', event);
        onChange?.(event);
        onDateChange?.('');
        console.log('onChange called with empty value');
      } else {
        setSelectedDate(date);
        setInputValue(format(date, 'yyyy/MM/dd'));
        // Convert to yyyy-MM-dd format for form submission
        const formattedDate = format(date, 'yyyy-MM-dd');
        const event = { target: { value: date } };
        console.log('Triggering onChange with value:', formattedDate);
        console.log('Event object:', event);
        onChange?.(event);
        onDateChange?.(formattedDate);
        console.log('onChange called with value:', formattedDate);
      }
      dialogRef.current?.close();
    };

    // Handle input click to open dialog
    const handleInputClick = () => {
      toggleDialog();
    };

    return (
      <div style={{ position: 'relative' }}>
        <input
          ref={ref}
          style={{ fontSize: 'inherit' }}
          id='date-input'
          type='text'
          value={inputValue}
          placeholder='YYYY/MM/DD'
          readOnly
          onClick={handleInputClick}
          name={props.name}
          {...props}
        />
        {/* Hidden input for React Hook Form */}
        <input type='hidden' name={props.name} value={value || ''} />{' '}
        <button
          type='button'
          style={{
            fontSize: 'inherit',
            pointerEvents: 'none',
            position: 'absolute',
            top: '50%',
            right: '10px',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '0',
            margin: '0',
          }}
          onClick={toggleDialog}
          aria-controls='dialog'
          aria-haspopup='dialog'
          aria-expanded={isDialogOpen}
          aria-label='Open calendar to choose booking date'
        >
          <img
            className='c-icon-calendar'
            src='/img/common/ic-calendar.png'
            alt='calendar'
          />
        </button>
        <dialog
          role='dialog'
          ref={dialogRef}
          id={dialogId}
          aria-modal
          aria-labelledby={headerId}
          onClose={() => setIsDialogOpen(false)}
        >
          <DayPicker
            month={month}
            onMonthChange={setMonth}
            autoFocus
            mode='single'
            selected={selectedDate}
            onSelect={handleDayPickerSelect}
            locale={ja}
            disabled={minDate ? (date) => date < minDate : undefined}
          />
        </dialog>
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';

export default DatePicker;
